import { MenuItem, Typography } from '@mui/material';
import { PopupState } from 'material-ui-popup-state/core';
import React, { SyntheticEvent, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { API } from 'aws-amplify';
import {
  setHoverTeam,
  setRouteConfirmationOrder,
  updateDeparturePadThunk,
  updateOrderStatus,
  updateTagsThunk,
  updateArrivalPadThunk
} from 'actions/orderActions';
import { getActiveCraft } from 'selectors/liveAircraftSelectors';
import { ChevronLeft, Check } from '@mui/icons-material';
import { AddressFind, Order } from '@m4a3/api-client';
import { rerouteOrderThunk } from 'actions/adminActions';

import {
  selectHoverTeam,
  selectLanesAndData,
  selectTeamsAndData,
  invalidatingAnOrdersDropLocation,
  selectPadStatusMap
} from 'selectors/ordersSelector';
import { Box } from '@mui/system';
import { useAppSelector } from 'hooks/redux';
import { getNextStatus, getStatusData } from '../../../services/order-service';
import { LoadingSpinner } from '../../../components/LoadingSpinner/LoadingSpinner';
import { getDeliveryLocationContent, getShortIdForOrder, shouldShowDropPointConfirmationForOrder } from '../../../utils/orderUtils';
import { getAllActivePadsForOrder } from '../../../utils/landingPadUtils';
import { logToDatadog } from '../../../services/datadog';
import { Circle, MapContainer, Pane, TileLayer } from 'react-leaflet';
import { dropPointRadiusInMetres, espooCellId } from '../../../constants';
import { orderfulApi } from '../../../services/api-instances';
import { Chip } from '@mui/material';

interface OrderCardContextProps {
  popupState: PopupState;
  order: Order;
  subMenu?: 'lane' | 'aircraft' | 'team' | 'vendor' | 'drop-point' | 'arrival-pad';
  tags?: string[];
  onClickShowSuggestedDropModalForOrder?: (orderId: string) => void;
  onClickInvalidateDropLocation?: (orderId: string, locationId: string) => void;
  aircraftData?: Record<string, any>;
  pads: any[];
}

export const OrderCardContext = (props: OrderCardContextProps) => {

  const [content, setContent] = useState(props.subMenu || '');
  const [addressFind, setAddressFind] = useState<AddressFind | null>(null);
  const [isLoadingAddressFind, setIsLoadingAddressFind] = useState(false);
  const [wasInvalidating, setWasInvalidating] = useState(false);
  const dispatch = useDispatch();
  const { pads } = props;

  const acList = useSelector(getActiveCraft);
  const teamData = useAppSelector(state =>
    selectTeamsAndData(state, props.order)
  );
  const laneData = useAppSelector(state =>
    selectLanesAndData(state, props.order)
  );
  const hoverTeam = useSelector(selectHoverTeam);
  const invalidtingDropLocation = useSelector(invalidatingAnOrdersDropLocation);
  const padStatusMap = useSelector(selectPadStatusMap);

  const next = props.order?.tags?.includes('NEXT');
  const priority = props.order?.tags?.includes('PRIORITY');

  // Fetch addressFind data when component mounts
  useEffect(() => {
    const fetchAddressFind = async () => {
      if (!props.order?.eircode) return;
      
      setIsLoadingAddressFind(true);
      try {
        const addressFindData = await API.get('api', `/addressfind/locationId/${props.order.eircode}`, {});
        setAddressFind(addressFindData);
      } catch (error) {
        console.error('Error fetching addressfind data:', error);
        setAddressFind(null);
      } finally {
        setIsLoadingAddressFind(false);
      }
    };

    fetchAddressFind();
  }, [props.order?.eircode]);

  // Watch for invalidating drop location state changes to determine when to close this component 
  useEffect(() => {
    if (invalidtingDropLocation && !wasInvalidating) {
      // Started invalidating
      setWasInvalidating(true);
    } else if (!invalidtingDropLocation && wasInvalidating) {
      // Finished invalidating - close the modal
      setWasInvalidating(false);
      props.popupState.close();
    }
  }, [invalidtingDropLocation, wasInvalidating, props.popupState]);

  const chooseDeparturePad = (event: SyntheticEvent<any>) => {
    event.stopPropagation();
    setContent('aircraft');
  };

  const loadFoodToDrone = (event: SyntheticEvent<any>) => {
    event.stopPropagation();
    const newTags = [...(props.order?.tags || []), 'LOAD_NOW'];
    dispatch(updateTagsThunk(props.order.id, newTags));
    props.popupState.close();
  };

  const chooseTeam = (event: SyntheticEvent<any>) => {
    event.stopPropagation();
    setContent('team');
  };
  const vendorCommand = (event: SyntheticEvent<any>) => {
    event.stopPropagation();
    setContent('vendor');
  };

  const markAsNext = async (event: SyntheticEvent<any>) => {
    event.stopPropagation();
    const tagToAdd = 'NEXT';
    if (props?.tags?.includes(tagToAdd)) {
      await dispatch(
        updateTagsThunk(
          props.order.id,
          props.tags.filter(tag => tag !== 'NEXT')
        )
      );
    } else if (!props?.tags) {
      await dispatch(updateTagsThunk(props.order.id, [tagToAdd]));
    } else {
      const newTags = [...props?.tags, tagToAdd];
      await dispatch(updateTagsThunk(props.order.id, newTags));
    }

    props.popupState.close();
  };
  const markAsPriority = async (event: SyntheticEvent<any>) => {
    event.stopPropagation();
    const tagToAdd = 'PRIORITY';
    if (props?.tags?.includes(tagToAdd)) {
      await dispatch(
        updateTagsThunk(
          props.order.id,
          props.tags.filter(tag => tag !== 'PRIORITY')
        )
      );
    } else if (!props?.tags) {
      await dispatch(updateTagsThunk(props.order.id, [tagToAdd]));
    } else {
      const newTags = [...props?.tags, tagToAdd];
      await dispatch(updateTagsThunk(props.order.id, newTags));
    }

    props.popupState.close();
  };

  const selectDeparturePad = (pad: string | null) => async (event: SyntheticEvent<any>) => {
    event.stopPropagation();
    try {
      await dispatch(updateDeparturePadThunk(props.order.id, pad));
      // Also set the arrival pad to the same value as the departure pad when first selected
      // TODO: Re-enable this once we're happy with asymmetric routes using same pad
      //if (pad && !props.order.destinationLandingPad) {
      await dispatch(updateArrivalPadThunk(props.order.id, pad));
      //}
      // Only reroute if both pads will be set (in this case, both are set to the same value)
      if (pad) {
        await dispatch(rerouteOrderThunk(props.order?.id));
      }
      props.popupState.close();
    } catch (err) {
      logToDatadog('Error setting departure pad. Error:', {err})
      props.popupState.close();
    }
  };

  const onClickUpdateStatus = async (event: SyntheticEvent<any>) => {
    event.stopPropagation();
    const nextStatus = getNextStatus(props.order.status);
    if (nextStatus === 'READY') {
      dispatch(setRouteConfirmationOrder(props.order));
    } else {
      await dispatch(updateOrderStatus(props.order, nextStatus));
      props.popupState.close();
    }
  };

  const onClickReroute = async (event: SyntheticEvent<any>) => {
    event.stopPropagation();

    try {
      await dispatch(rerouteOrderThunk(props.order.id));
    } catch (err) {
      console.log(err);
    }
    props.popupState.close();
  };
  const back = (event: SyntheticEvent<any>) => {
    event.stopPropagation();
    setContent('');
  };

  const clickTag = (newTag: string) => async (event: SyntheticEvent<any>) => {
    event.stopPropagation();

    const orderHasTag = props.tags?.includes(newTag);
    if (orderHasTag) {
      dispatch(
        updateTagsThunk(
          props.order.id,
          props.tags?.filter(tag => {
            return tag !== newTag;
          })
        )
      );
    } else if (!orderHasTag) {
      if (!props.tags) {
        dispatch(updateTagsThunk(props.order.id, [newTag]));
      } else {
        const newTags = [
          ...props.tags?.filter(tag => !tag.includes('TEAM')),
          newTag
        ];
        dispatch(updateTagsThunk(props.order.id, newTags));
      }
    }

    props.popupState.close();
  };
  const clickLaneTag = (newTag: string) => async (
    event: SyntheticEvent<any>
  ) => {
    event.stopPropagation();

    const orderHasTag = props.tags?.includes(newTag);
    if (orderHasTag) {
      dispatch(
        updateTagsThunk(
          props.order.id,
          props.tags?.filter(tag => {
            return tag !== newTag;
          })
        )
      );
    } else if (!orderHasTag) {
      if (!props.tags) {
        dispatch(updateTagsThunk(props.order.id, [newTag]));
      } else {
        const newTags = [
          ...props.tags?.filter(tag => !tag.includes('LANE')),
          newTag
        ];
        dispatch(updateTagsThunk(props.order.id, newTags));
      }
    }

    props.popupState.close();
  };
  const noOp = (event: SyntheticEvent<any>) => {
    event.stopPropagation();
  };

  const humanFriendlyTeamName = (teamName: string): string => {
    return teamName?.replace('TEAM', 'Team ') || ''; // TEAM1 => Team 1
  };

  const extraDelivery = async (event: SyntheticEvent<any>) => {
    event.stopPropagation();
    try {
      await orderfulApi.createExtraDeliveryOrder(props.order.id);
    } catch (err) {
    }
  };

  const getSuggestedDropsButton = () => {
    
    if (props.order.cellId !== espooCellId) {
      return (
        <button
          type="button"
          className="mt-1 rounded-md bg-blue-600 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 text-center w-full"
          onClick={() => props.onClickShowSuggestedDropModalForOrder(props.order.id)} >
            Suggest drops
        </button>
      )
    } else { 
      return null;
    }
  }

  const getContentForSelectedTeam = (): JSX.Element => {
    const status = props?.order?.teamStatus;
    if (status === 'NEW') {
      return (
        <>
          <LoadingSpinner size={20} />
          <Typography>Waiting to accept</Typography>
        </>
      );
    } else if (status === 'ON_THE_WAY') {
      return <Typography>🚗 Driving to Eircode</Typography>;
    } else if (status === 'ARRIVED') {
      return <Typography>🏡 At delivery spot</Typography>;
    } else if (status === 'DROP_SAFE') {
      return <Typography>✅ Delivery spot is safe</Typography>;
    } else if (status === 'DROP_SUGGESTED') {
      if (props.order?.suggestedDrop !== '0.0,0.0') {
        return <Typography>📍 Delivery spot suggested</Typography>;
      }
    } else if (status === 'DROP_REJECTED') {
      return <Typography>❌ Delivery spot rejected</Typography>;
    } else if (status === 'AWAITING_DROP') {
      return <Typography>✅ Delivery spot set</Typography>;
    } else if (status === 'COMPLETE') {
      return <Typography>✅ Delivery spot set</Typography>;
    }

    return (
      <>
        <LoadingSpinner size={20} />
        <Typography>Waiting to accept</Typography>
      </>
    );
  };

  // Get pad status from memoized selector - much more efficient
  const getPadStatus = (padId: number) => {
    return padStatusMap.get(padId) || { color: null, label: null, count: 0 };
  };

  const getContentForPadMenuItem = (pad: any) => {
    const padStatus = getPadStatus(pad.id);
    const statusColor = padStatus.color;
    const statusLabel = padStatus.label;
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body1" color="text.primary">{pad.name}</Typography>
          {pad.aircraft ? (
            <Typography variant="body2" color="text.secondary" sx={{ marginLeft: 1 }}>(MNA-{pad.aircraft})</Typography>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {statusColor && statusLabel && (
            <Chip
              label={statusLabel}
              size="small"
              sx={{
                backgroundColor: statusColor,
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.65rem',
                height: '20px',
                '& .MuiChip-label': {
                  paddingLeft: 1,
                  paddingRight: 1
                }
              }}
            />
          )}
        </Box>
      </Box>
    );
  };

  const chooseArrivalPad = (event: SyntheticEvent<any>) => {
    event.stopPropagation();
    setContent('arrival-pad');
  };

  // Use shared utility function for pad filtering logic
  const getAllActivePads = () => getAllActivePadsForOrder(props.order, pads || []);

  const selectArrivalPad = (pad: string | null) => async (event: SyntheticEvent<any>) => {
    event.stopPropagation();
    try {
      await dispatch(updateArrivalPadThunk(props.order.id, pad));
      // Only reroute if both pads will be set (departure pad already exists and arrival pad is being set)
      if (pad && props.order.originLandingPadId) {
        await dispatch(rerouteOrderThunk(props.order?.id));
      }
      props.popupState.close();
    } catch (err) {
      logToDatadog('Error setting arrival pad. Error:', {err})
      props.popupState.close();
    }
  };

  if (content === '') {
    const nextStatus = getNextStatus(props.order.status);
    return (
      <>
        <MenuItem
          onClick={noOp}
          disableRipple
          sx={{
            ':hover': {
              backgroundColor: 'transparent',
              cursor: 'default'
            }
          }}>
          <Typography variant="h6" style={{ fontSize: 16, fontWeight: 600 }}>
            #{getShortIdForOrder(props.order)}{' '}
          </Typography>
        </MenuItem>
        {['RECEIVED_FROM_STORE', 'READY'].includes(props.order.status) &&
        !props.order.tags?.includes('LOAD_NOW') ? (
          <MenuItem onClick={loadFoodToDrone}>Load food to drone</MenuItem>
        ) : (
          <></>
        )}
        <MenuItem onClick={chooseTeam}>Assign Team</MenuItem>
        <MenuItem onClick={chooseDeparturePad}>Assign Departure Pad</MenuItem>
        {props.order.aircraft ? (
          <MenuItem onClick={chooseArrivalPad}>Assign Arrival Pad</MenuItem>
        ) : null}
        <MenuItem onClick={vendorCommand}>Vendor Comms</MenuItem>
        <MenuItem onClick={extraDelivery}>Extra Delivery</MenuItem>
        <MenuItem onClick={markAsNext}>
          Next Order
          {next ? (
            <Check style={{ display: 'inline-block', cursor: 'pointer' }} />
          ) : null}
        </MenuItem>
        <MenuItem onClick={markAsPriority}>
          Prority Order (Show Top of List)
          {priority ? (
            <Check style={{ display: 'inline-block', cursor: 'pointer' }} />
          ) : null}
        </MenuItem>
        {nextStatus ? (
          <MenuItem
            onClick={onClickUpdateStatus}>{`Update to ${nextStatus}`}</MenuItem>
        ) : null}

        <MenuItem onClick={onClickReroute}>{`Reroute this order`}</MenuItem>
      </>
    );
  }
  if (content === 'team') {
    const selectedTeamData = teamData.find(
      td => td.tag === props.order?.tags?.find(tag => tag.includes('TEAM'))
    );
    return (
      <>
        <MenuItem
          onClick={noOp}
          disableRipple
          sx={{
            ':hover': {
              backgroundColor: 'transparent',
              cursor: 'default'
            }
          }}>
          {selectedTeamData ? (
            <Box
              display={'flex'}
              sx={{ width: '100%' }}
              flexDirection="row"
              color={'#24292F'}
              alignContent={'space-between'}
              justifyContent={'space-between'}>
              <Box display={'flex'}>
                <Typography fontWeight={'500'}>
                  {humanFriendlyTeamName(selectedTeamData.tag)}{' '}
                  {selectedTeamData.distance ? '' : ' (Offline)'}
                </Typography>
              </Box>
              <Box
                display={'flex'}
                width={48}
                bgcolor="#EFF0F6"
                alignContent={'center'}
                justifyContent={'center'}
                borderRadius={16}>
                <Typography
                  variant="h6"
                  sx={{ height: '100%' }}
                  color={'#24292F'}
                  textAlign={'center'}>
                  {selectedTeamData.orderCount}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography variant="h6" style={{ fontSize: 16, fontWeight: 600 }}>
              Assign Team
            </Typography>
          )}
        </MenuItem>
        {selectedTeamData ? (
          <MenuItem
            onClick={noOp}
            disableRipple
            sx={{
              ':hover': {
                backgroundColor: 'transparent',
                cursor: 'default'
              }
            }}>
            <Box
              sx={{
                width: '100%',
                border: '1px solid #D9DBE9',
                borderRadius: '8px',
                padding: '12px'
              }}>
              <Box display={'flex'}>{getContentForSelectedTeam()}</Box>
            </Box>
          </MenuItem>
        ) : (
          <></>
        )}
        {shouldShowDropPointConfirmationForOrder(props.order) ? <MenuItem onClick={() => {
          dispatch(setRouteConfirmationOrder(props.order))
        }}>
        <Box
          sx={{
            width: '100%',
            border: '1px solid #D9DBE9',
            borderRadius: '8px',
            background: '#0583FF',
            padding: '12px',
            color: 'white'
          }}
          display={'block'}>
          <Box display={'flex'}>
            <Typography fontWeight={600} fontSize={12} color={'white'}>Suggest delivery spot</Typography>
          </Box>
        </Box>
        </MenuItem> : <></>}

        <MenuItem onClick={back}>
          <ChevronLeft style={{ display: 'inline-block', cursor: 'pointer' }} />
        </MenuItem>
        {teamData.map(teamData => {
          const mouseEnterTeam = () => {
            if (props.order) {
              if (hoverTeam === teamData.tag) return;
              dispatch(setHoverTeam(teamData.tag));
            }
          };
          const mouseExitTeam = () => {
            if (hoverTeam === null) return;
            dispatch(setHoverTeam(null));
          };
          return (
            <MenuItem
              key={`order-card-context-team-menu-${teamData.tag}`}
              onClick={clickTag(teamData.tag)}
              onMouseEnter={mouseEnterTeam}
              onMouseLeave={mouseExitTeam}>
              <Box
                display={'flex'}
                sx={{ width: '100%' }}
                flexDirection="row"
                color={'#24292F'}
                alignContent={'space-between'}
                justifyContent={'space-between'}>
                <Box display={'flex'}>
                  <Typography fontWeight={'500'}>
                    {humanFriendlyTeamName(teamData.tag)}{' '}
                    {teamData.distance
                      ? ` ${teamData.distance.toFixed(2)}km`
                      : '• Offline'}
                  </Typography>
                </Box>
                <Box
                  display={'flex'}
                  width={48}
                  bgcolor="#EFF0F6"
                  alignContent={'center'}
                  justifyContent={'center'}
                  borderRadius={16}>
                  <Typography
                    variant="h6"
                    sx={{ height: '100%' }}
                    color={'#24292F'}
                    textAlign={'center'}>
                    {teamData.orderCount}
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          );
        })}
      </>
    );
  }
  if (content === 'lane') {
    return (
      <>
        <MenuItem
          onClick={noOp}
          disableRipple
          sx={{
            ':hover': {
              backgroundColor: 'transparent',
              cursor: 'default'
            }
          }}>
          <Typography variant="h6" style={{ fontSize: 16, fontWeight: 600 }}>
            #{getShortIdForOrder(props.order)} - Assign Lane
          </Typography>
        </MenuItem>
        <MenuItem onClick={back}>
          <ChevronLeft style={{ display: 'inline-block', cursor: 'pointer' }} />
        </MenuItem>

        {laneData.map(laneData => {
          return (
            <MenuItem
              key={`order-card-context-lane-menu-${laneData.tag}`}
              onClick={clickLaneTag(laneData.tag)}>
              <Box
                display={'flex'}
                sx={{ width: '100%' }}
                flexDirection="row"
                alignContent={'space-between'}
                justifyContent={'space-between'}>
                <Box display={'flex'}>
                  <Typography>{laneData.tag}</Typography>
                </Box>
                <Box
                  display={'flex'}
                  width={48}
                  mx={1}
                  bgcolor="gray"
                  alignContent={'center'}
                  justifyContent={'center'}
                  borderRadius={16}>
                  <Typography
                    variant="h6"
                    sx={{ height: '100%' }}
                    color={'white'}
                    textAlign={'center'}>
                    {laneData.orderCount}
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          );
        })}
      </>
    );
  }
  if (content === 'aircraft') {
    return (
      <>
        <MenuItem
          onClick={noOp}
          disableRipple
          sx={{
            ':hover': {
              backgroundColor: 'transparent',
              cursor: 'default'
            }
          }}>
          <Typography variant="h6" style={{ fontSize: 16, fontWeight: 600 }}>
            #{getShortIdForOrder(props.order)} - Assign Departure Pad
          </Typography>
        </MenuItem>
        <MenuItem onClick={back}>
          <ChevronLeft style={{ display: 'inline-block', cursor: 'pointer' }} />
        </MenuItem>
        <MenuItem onClick={selectDeparturePad(null)} style={{}} value={'None'}>
          None
        </MenuItem>
        {
          getAllActivePads()
          .map(pad => {
            return (
              <MenuItem
                key={`order-card-context-pad-menu-${pad.id}`}
                onClick={selectDeparturePad(pad.id.toString())}
                disabled={!pad.aircraft}
                style={{}}
                value={pad.id}>
                {getContentForPadMenuItem(pad)}
              </MenuItem>
            );
          })
        }
      </>
    );
  }
  if (content === 'arrival-pad') {
    const cellPads = getAllActivePads();
    return (
      <>
        <MenuItem
          onClick={noOp}
          disableRipple
          sx={{
            ':hover': {
              backgroundColor: 'transparent',
              cursor: 'default'
            }
          }}>
          <Typography variant="h6" style={{ fontSize: 16, fontWeight: 600 }}>
            #{getShortIdForOrder(props.order)} - Assign Arrival Pad
          </Typography>
        </MenuItem>
        <MenuItem onClick={back}>
          <ChevronLeft style={{ display: 'inline-block', cursor: 'pointer' }} />
        </MenuItem>
        <MenuItem onClick={selectArrivalPad(null)} style={{}} value={'None'}>
          None
        </MenuItem>
        {cellPads.length > 0 ? (
          cellPads.map(pad => {
            return (
              <MenuItem
                key={`order-card-context-arrival-pad-menu-${pad.id}`}
                onClick={selectArrivalPad(pad.id.toString())}
                disabled={!pad.aircraft}
                style={{}}
                value={pad.id}>
                {getContentForPadMenuItem(pad)}
              </MenuItem>
            );
          })
        ) : (
          <MenuItem disabled style={{ color: 'gray' }}>
            No available pads in this cell
          </MenuItem>
        )}
      </>
    );
  }
  if (content === 'drop-point') {
    return (
      <>
        <MenuItem
          onClick={noOp}
          disableRipple
          sx={{
            ':hover': {
              backgroundColor: 'transparent',
              cursor: 'default'
            },
            width: 252,
            borderColor: 'red',
            borderWidth: 1,
          }}>
          <Typography variant="h6" style={{ fontSize: 16, fontWeight: 600 }}>
            Drop location
          </Typography>
        </MenuItem>
        <div style={{
          borderRadius: 8,
          padding: 12,
          margin: 16,
          border: '1px solid #D9DBE9',
          background: '#FFF',
          boxShadow: '0px 8px 16px 0px rgba(17, 17, 17, 0.04)',
          maxWidth: 220,
        }}>
          {isLoadingAddressFind ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
              <LoadingSpinner size={20} />
              <Typography variant="body2" sx={{ marginLeft: 1 }}>Loading location...</Typography>
            </div>
          ) : (
            getDeliveryLocationContent(addressFind)
          )}
        </div>
        <div style={{
          marginLeft: 16
        }}>
          {isLoadingAddressFind ? (
            <div style={{ 
              height: 220,
              width: 220,
              borderRadius: 8,
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              border: '1px solid #D9DBE9',
              background: '#f5f5f5'
            }}>
              <LoadingSpinner size={20} />
            </div>
          ) : (
            addressFind?.dropLocation ? <MapContainer
              style={{
                height: 220,
                width: 220,
                borderRadius: 8,
              }}
              whenReady={function() {
                // zoom prop is not changing the initial zoom past 17, so this is the workaround.
                this.setZoom(20);
              }}
              center={[addressFind?.dropLocation.latitude, addressFind?.dropLocation.longitude]}
              zoom={17}>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <TileLayer
                url="https://tiles.manna.aero/{z}/{x}/{y}.png"
                maxZoom={24}
              />
              <Pane name={`order-${props.order?.id}-${addressFind?.dropLocation.id}`} key={`order-${props.order?.id}-${addressFind?.dropLocation.id}`}>
                <Circle
                  center={[addressFind?.dropLocation.latitude, addressFind?.dropLocation.longitude]}
                  radius={dropPointRadiusInMetres / 2}
                  fillOpacity={0.8}
                  pathOptions={{color: '#0583FF'}}>
                </Circle>
              </Pane>
            </MapContainer> : <></>
          )}
        </div>
        <div style={{
          padding: 8,
          justifyContent: 'center',
          alignItems: 'center',
          gap: 8,
          flexShrink: 0,
        }}>
          {addressFind?.dropLocation ? ( 
            <button
              type="button"
              className="mt-1 rounded-md bg-red-800 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-800 text-center w-full flex items-center justify-center"
              onClick={(event) => {
                event.stopPropagation();
                props.onClickInvalidateDropLocation(props.order.id, props.order.eircode);
              }}
            >
              {invalidtingDropLocation === true ? (
                <LoadingSpinner size={20} />
              ) : (
                'Invalidate drop location'
              )}
            </button>
          ) :(
            getSuggestedDropsButton()
          )}
        </div>
      </>
    );
  }
  return null;
};
